import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlass,
  CheckCircle,
  WarningCircle,
  Plus,
  CaretRight,
  Trash,
  Storefront,
} from '@phosphor-icons/react';
import {
  PosLogo,
  BuyButtonLogo,
  AffiliatesLogo,
} from '../components/icons/BrandIcons';
import { MERCHANT_ROUTES, useAuthStore, apiClient, API_ENDPOINTS, useUpgradeStore, SALES_CHANNEL_LIMITS } from '@dukene/shared';

function AssetIcon({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return <img src={src} alt={alt} className={className} />;
}

interface ChannelItem {
  id: string;
  title: string;
  subtitle: string;
  category: 'advertising' | 'social' | 'retail' | 'partners';
  description: string;
  logos: React.ReactNode;
  status: 'connected' | 'setup_needed' | 'available';
  href: string;
  metrics?: { label: string; value: string; positive?: boolean }[];
}

export default function SalesChannels() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeStoreId, user } = useAuthStore();
  const currentPlan = user?.plan || 'BASIC';
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'connected' | 'available'>('all');

  const { data: store } = useQuery<any>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: async () => apiClient.get<any>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!activeStoreId,
  });

  const [userAddedChannels, setUserAddedChannels] = useState<string[]>(() => {
    try {
      if (!activeStoreId) return [];
      const saved = localStorage.getItem(`user-added-sales-channels-${activeStoreId}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!activeStoreId) {
      setUserAddedChannels([]);
      return;
    }
    try {
      const saved = localStorage.getItem(`user-added-sales-channels-${activeStoreId}`);
      setUserAddedChannels(saved ? JSON.parse(saved) : []);
    } catch {
      setUserAddedChannels([]);
    }
  }, [activeStoreId]);

  useEffect(() => {
    if (!activeStoreId) return;
    const s = store as any;
    if (s?.settings && Array.isArray(s.settings.installedChannels)) {
      setUserAddedChannels((prev) => {
        const merged = Array.from(new Set([...s.settings.installedChannels, ...prev]));
        try {
          localStorage.setItem(`user-added-sales-channels-${activeStoreId}`, JSON.stringify(merged));
        } catch {}
        return merged;
      });
    }
  }, [store, activeStoreId]);

  const installedChannelIds = useMemo(() => {
    if (!activeStoreId || !store) return [];
    const s = store as any;
    const dbInstalled: string[] = Array.isArray(s?.settings?.installedChannels)
      ? s.settings.installedChannels
      : userAddedChannels;
    const ids: string[] = Array.from(new Set([...dbInstalled, ...userAddedChannels]));
    if (s?.status === 'ACTIVE' && !ids.includes('online_store')) ids.push('online_store');
    if (s?.googleMerchantId && !ids.includes('google')) ids.push('google');
    if (s?.metaCommerceEnabled && !ids.includes('meta')) ids.push('meta');
    if (s?.whatsappConnected && !ids.includes('whatsapp')) ids.push('whatsapp');
    return ids;
  }, [store, userAddedChannels, activeStoreId]);

  const handleInstallChannel = async (channelId: string, href: string) => {
    if (!activeStoreId) return;

    const maxChannels = (SALES_CHANNEL_LIMITS as any)[currentPlan] ?? 1;

    if (channelId !== 'online_store' && currentPlan === 'BASIC') {
      useUpgradeStore.getState().open('sales_channel_limit');
      return;
    }

    if (installedChannelIds.length >= maxChannels && !installedChannelIds.includes(channelId)) {
      useUpgradeStore.getState().open('sales_channel_limit');
      return;
    }

    const nextIds = Array.from(new Set([...installedChannelIds, channelId]));
    setUserAddedChannels(nextIds);
    try {
      localStorage.setItem(`user-added-sales-channels-${activeStoreId}`, JSON.stringify(nextIds));
    } catch {}

    if (activeStoreId) {
      try {
        await apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, {
          settings: {
            ...((store as any)?.settings || {}),
            installedChannels: nextIds,
          },
        });
        queryClient.invalidateQueries({ queryKey: ['merchant-store'] });
      } catch (err) {
        console.error('Failed to save installed channel to database:', err);
      }
    }
    navigate(href);
  };

  const handleUninstallChannel = async (channelId: string) => {
    if (!activeStoreId) return;
    const nextIds = installedChannelIds.filter((id) => id !== channelId);
    setUserAddedChannels(nextIds);
    try {
      localStorage.setItem(`user-added-sales-channels-${activeStoreId}`, JSON.stringify(nextIds));
    } catch {}

    if (activeStoreId) {
      try {
        await apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, {
          settings: {
            ...((store as any)?.settings || {}),
            installedChannels: nextIds,
          },
        });
        queryClient.invalidateQueries({ queryKey: ['merchant-store'] });
      } catch (err) {
        console.error('Failed to uninstall channel from database:', err);
      }
    }
  };

  const channels: ChannelItem[] = useMemo(() => {
    const s = store as any;
    return [
      {
        id: 'online_store',
        title: 'Online Store',
        subtitle: 'Hosted E-commerce Storefront & Themes',
        category: 'retail',
        description: 'Publish a fully custom brand storefront on your reserved subdomain or custom domain with themes, SEO, and fast checkout.',
        logos: <Storefront weight="fill" className="w-6 h-6 text-[#006838]" />,
        status: s?.status === 'ACTIVE' ? 'connected' : (installedChannelIds.includes('online_store') ? 'setup_needed' : 'available'),
        href: '/store',
        metrics: s?.status === 'ACTIVE' ? [
          { label: 'Store Status', value: 'Published & Live', positive: true },
          { label: 'Domain', value: `${s?.subdomain || 'shop'}.dukene.com`, positive: true },
        ] : [
          { label: 'Store Status', value: 'Reserved / Offline', positive: false },
        ],
      },
      {
        id: 'google',
        title: 'Google & YouTube',
        subtitle: 'Google Merchant Center & Shopping Feeds',
        category: 'advertising',
        description: 'Automatically sync your catalog to free Google Shopping listings, Google Search results, and Performance Max campaigns.',
        logos: <AssetIcon src="/icons/google-icon.svg" alt="Google" className="w-6 h-6" />,
        status: s?.googleMerchantId ? 'connected' : (installedChannelIds.includes('google') ? 'setup_needed' : 'available'),
        href: MERCHANT_ROUTES.GOOGLE_COMMERCE,
        metrics: s?.googleMerchantId ? [
          { label: 'Free Listings', value: 'Ready to publish', positive: false },
          { label: 'Feed Sync', value: 'Automatic sync', positive: true },
        ] : undefined,
      },
      {
        id: 'meta',
        title: 'Facebook & Instagram',
        subtitle: 'Meta Commerce Manager & Shops',
        category: 'social',
        description: 'Sell directly across Facebook Shop and Instagram Shopping. Tag products in posts, stories, and track conversions with Meta Pixel.',
        logos: (
          <div className="flex items-center -space-x-1.5">
            <AssetIcon src="/icons/facebook.svg" alt="Facebook" className="w-6 h-6 relative z-10" />
            <AssetIcon src="/icons/ig-instagram-icon.svg" alt="Instagram" className="w-6 h-6" />
          </div>
        ),
        status: s?.metaCommerceEnabled ? 'connected' : (installedChannelIds.includes('meta') ? 'setup_needed' : 'available'),
        href: MERCHANT_ROUTES.META_COMMERCE,
        metrics: s?.metaCommerceEnabled ? [
          { label: 'Catalog Status', value: 'Active & Linked', positive: true },
          { label: 'Conversion Pixel', value: 'Receiving events', positive: true },
        ] : undefined,
      },
      {
        id: 'whatsapp',
        title: 'WhatsApp Catalog',
        subtitle: 'WhatsApp Business API & Conversational Checkout',
        category: 'social',
        description: 'Deploy native shoppable product catalogs inside WhatsApp chats, enable automated checkouts, and broadcast promotional campaigns.',
        logos: <AssetIcon src="/icons/whatsapp-icon.svg" alt="WhatsApp" className="w-6 h-6" />,
        status: s?.whatsappConnected ? 'connected' : (installedChannelIds.includes('whatsapp') ? 'setup_needed' : 'available'),
        href: MERCHANT_ROUTES.WHATSAPP_CATALOG,
        metrics: s?.whatsappConnected ? [
          { label: 'WhatsApp Shop', value: 'Live Catalog', positive: true },
          { label: 'Chat Checkout', value: 'Instant cart sync', positive: true },
        ] : undefined,
      },
      {
        id: 'pos',
        title: 'Point of Sale (POS)',
        subtitle: 'In-Person & Retail Checkout',
        category: 'retail',
        description: 'Sell in person at retail locations, weekend pop-ups, and markets with barcode scanning, receipt printers, and synchronized inventory.',
        logos: <PosLogo className="w-6 h-6" />,
        status: installedChannelIds.includes('pos') ? 'connected' : 'available',
        href: '/pos',
      },
      {
        id: 'buy_button',
        title: 'Buy Button',
        subtitle: 'Embeddable Checkout Cards & Widgets',
        category: 'retail',
        description: 'Create customizable product cards and instant checkout buttons to embed on any blog, WordPress site, or external landing page.',
        logos: <BuyButtonLogo className="w-6 h-6" />,
        status: installedChannelIds.includes('buy_button') ? 'connected' : 'available',
        href: '/buy-button',
      },
      {
        id: 'affiliates',
        title: 'Affiliates & Creator Network',
        subtitle: 'Partner Referral & Commission Engine',
        category: 'partners',
        description: 'Scale revenue through commission-based influencers, referral tracking links, and automated commission payouts.',
        logos: <AffiliatesLogo className="w-6 h-6" />,
        status: installedChannelIds.includes('affiliates') ? 'connected' : 'available',
        href: '/affiliates',
      },
    ];
  }, [store, installedChannelIds]);

  const filteredChannels = useMemo(() => {
    return channels.filter((ch) => {
      const matchesSearch =
        ch.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ch.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ch.description.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      if (activeFilter === 'connected') return ch.status === 'connected' || ch.status === 'setup_needed';
      if (activeFilter === 'available') return ch.status === 'available';
      return true;
    });
  }, [channels, searchQuery, activeFilter]);

  const installedChannels = filteredChannels.filter((c) => c.status === 'connected' || c.status === 'setup_needed');
  const availableChannels = filteredChannels.filter((c) => c.status === 'available');

  return (
    <div className="max-w-[1040px] mx-auto pb-20 space-y-6">
      {/* Top Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
        <div>
          <h1 className="text-xl font-bold text-[#202223] dark:text-white tracking-tight">Sales channels</h1>
        </div>

        {/* Filter and Search */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-[#F1F2F4] dark:bg-slate-800 p-0.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700">
            {(['all', 'connected', 'available'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md capitalize transition-colors ${
                  activeFilter === filter
                    ? 'bg-white dark:bg-slate-900 text-[#202223] dark:text-white shadow-2xs'
                    : 'text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-white'
                }`}
              >
                {filter === 'connected' ? 'Installed' : filter}
              </button>
            ))}
          </div>

          <div className="relative w-56">
            <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5F62]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sales channels"
              className="w-full h-8 pl-8 pr-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-[#202223] dark:text-white placeholder-[#5C5F62] focus:outline-none focus:border-[#202223] dark:focus:border-slate-400"
            />
          </div>
        </div>
      </div>

      {/* Installed Channels Section */}
      {installedChannels.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
              Installed channels ({installedChannels.length})
            </h2>
          </div>

          <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl divide-y divide-[#E1E3E5] dark:divide-slate-800 shadow-2xs">
            {installedChannels.map((channel) => (
              <div
                key={channel.id}
                onClick={() => navigate(channel.href)}
                className="p-4 sm:p-5 hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 group"
              >
                <div className="flex items-start gap-3.5 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg border border-[#E1E3E5] dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center shrink-0 shadow-2xs mt-0.5">
                    {channel.logos}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-[#202223] dark:text-white group-hover:underline">
                        {channel.title}
                      </h3>
                      {channel.status === 'connected' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800/60">
                          <CheckCircle weight="fill" className="w-3 h-3 text-emerald-600" />
                          Connected
                        </span>
                      )}
                      {channel.status === 'setup_needed' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800/60">
                          <WarningCircle weight="fill" className="w-3 h-3 text-amber-600" />
                          Setup needed
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 leading-relaxed max-w-2xl">
                      {channel.description}
                    </p>

                    {/* Metrics row */}
                    {channel.metrics && (
                      <div className="flex items-center gap-6 mt-3 pt-3 border-t border-[#E1E3E5]/60 dark:border-slate-800/80">
                        {channel.metrics.map((m, i) => (
                          <div key={i} className="flex items-center gap-1.5">
                            <span className="text-[11px] font-medium text-[#5C5F62] dark:text-slate-400">
                              {m.label}:
                            </span>
                            <span className="text-[11px] font-semibold text-[#202223] dark:text-white">
                              {m.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(channel.href);
                    }}
                    className="px-3 py-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-[#202223] dark:text-white hover:bg-[#F6F6F7] dark:hover:bg-slate-700 transition-colors inline-flex items-center gap-1 shadow-2xs"
                  >
                    Manage
                    <CaretRight weight="bold" className="w-3 h-3 text-[#5C5F62]" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUninstallChannel(channel.id);
                    }}
                    className="p-1.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors shadow-2xs"
                    title={`Uninstall ${channel.title}`}
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Channels Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
            Available to install ({availableChannels.length})
          </h2>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl divide-y divide-[#E1E3E5] dark:divide-slate-800 shadow-2xs">
          {availableChannels.map((channel) => (
            <div
              key={channel.id}
              onClick={() => navigate(channel.href)}
              className="p-4 sm:p-5 hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-4 group"
            >
              <div className="flex items-start gap-3.5 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-lg border border-[#E1E3E5] dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center shrink-0 shadow-2xs mt-0.5">
                  {channel.logos}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-[#202223] dark:text-white group-hover:underline">
                      {channel.title}
                    </h3>
                  </div>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1 leading-relaxed max-w-2xl">
                    {channel.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInstallChannel(channel.id, channel.href);
                  }}
                  className="px-3.5 py-1.5 rounded-lg bg-[#202223] dark:bg-white text-white dark:text-slate-900 text-xs font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1.5 shadow-2xs"
                >
                  <Plus weight="bold" className="w-3.5 h-3.5" />
                  Install
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
